import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { BadgeTone } from '../components/ui';

/**
 * Overtime (operator, 2026-10-21): a department's leader sends it, an
 * administrator approves it, then the employee accepts or declines it. Payroll
 * pays the time worked that day beyond its hours, up to the minutes approved.
 */
export type OvertimeStatus = 'PENDING' | 'APPROVED' | 'ACCEPTED' | 'REJECTED' | 'DECLINED' | 'CANCELLED';

export const OVERTIME_STATUSES: OvertimeStatus[] = ['PENDING', 'APPROVED', 'ACCEPTED', 'REJECTED', 'DECLINED', 'CANCELLED'];

/** The most one day's overtime can be — the API's `MAX_OVERTIME_MINUTES`. */
export const MAX_OVERTIME_MINUTES = 12 * 60;

interface Person {
  id: string;
  fullName: string;
  fullNameAr: string | null;
}

/** What a day owes and, once judged, what was worked — `GET /overtime/preview`'s and the page's `day`. */
export interface OvertimeDay {
  kind: 'SHIFT' | 'DEFAULT' | 'OFF' | 'LEAVE' | 'HOLIDAY' | 'NONE';
  shift: { name: string; nameAr: string | null } | null;
  holiday: { name: string; nameAr: string | null } | null;
  owedMinutes: number | null;
  judged: boolean;
  workedMinutes: number | null;
  /** The time worked beyond the hours owed: what overtime on it can pay. */
  beyondMinutes: number | null;
}

export interface Overtime {
  id: string;
  userId: string;
  workDate: string;
  minutes: number;
  reason: string;
  status: OvertimeStatus;
  requestedById: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  answeredAt: string | null;
  answerNote: string | null;
  cancelledAt: string | null;
  createdAt: string;
  user: Person & { employeeCode: string | null };
  department: { id: string; name: string; nameAr: string | null } | null;
  requestedBy: Person | null;
  reviewedBy: Person | null;
  cancelledBy: Person | null;
  /** Accepted, its day judged: the minutes it pays. */
  paidMinutes: number | null;
  can: { decide: boolean; answer: boolean; cancel: boolean };
}

export interface OvertimeDetail extends Overtime {
  day: OvertimeDay;
  monthSignedOff: boolean;
}

/** Waiting for an administrator, for the employee, settled one way or the other. */
export function overtimeTone(status: OvertimeStatus): BadgeTone {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'APPROVED':
      return 'info';
    case 'ACCEPTED':
      return 'success';
    case 'REJECTED':
    case 'DECLINED':
      return 'error';
    default:
      return 'neutral';
  }
}

/**
 * How much overtime waits for the signed-in person: theirs to approve, and theirs
 * to answer — the sidebar's count beside Overtime and the tabs'. A new one rings
 * the bell too, so this keeps the bell's pace.
 */
export function useWaitingOvertime() {
  const { data } = useQuery({
    queryKey: ['overtime', 'waiting-count'],
    queryFn: () => api.get<{ review: number; answer: number; count: number }>('/overtime/waiting-count'),
    refetchInterval: 30_000,
  });
  return data ?? { review: 0, answer: 0, count: 0 };
}
